import { HubConnectionState } from '@microsoft/signalr';
import moment from 'moment-timezone';
import { lastValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';

import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';

import { getBrowserLang, TranslocoService } from '@jsverse/transloco';

import { MomentJsUtils } from '@utils/momentjs';

import { AppInfoService } from './app-info.service';
import { AppModeService } from './app-mode.service';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { OktaService } from './okta.service';
import { WebSocketEventService } from './web-socket-event.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private appInfoService = inject(AppInfoService);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private appModeService = inject(AppModeService);
  private oktaService = inject(OktaService);
  private authService = inject(AuthService);
  private title = inject(Title);
  private meta = inject(Meta);
  private translateService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);
  private dateAdapter = inject<DateAdapter<moment.Moment>>(DateAdapter<moment.Moment>);
  private document = inject<Document>(DOCUMENT);
  private webSocketEventService = inject(WebSocketEventService);

  private async loadEnvConfig() {
    await this.appInfoService.fetchEnv();
    const appInfo = this.appInfoService.appInfo;
    await this.oktaService.init(appInfo);
    /* eslint-disable-next-line no-console */
    console.info(`🔥 WattsOn ⚡ environment: ${appInfo.environment} version: ${appInfo.version}`);
  }

  private setupMoment() {
    moment.relativeTimeThreshold('s', 60);
    moment.relativeTimeThreshold('m', 60);
    moment.relativeTimeThreshold('h', 24);
    moment.relativeTimeThreshold('d', 30);
    moment.relativeTimeThreshold('M', 12);

    MomentJsUtils.setupPluginNativeUTC();
    MomentJsUtils.setupPluginStartOfCommodity();
    MomentJsUtils.setupPluginShowCommercialMarketProduct();
    MomentJsUtils.setupPluginShowCommercialEndDate();
    MomentJsUtils.setupPluginShowDateString();

    // We use default setting for english locale from here: https://momentjs.com/docs/#/customization/long-date-formats/
    // except for the L setting, we want french format and not english one...
    // Default is: https://github.com/moment/moment/blob/develop/src/locale/es-us.js
    moment.updateLocale('en', {
      longDateFormat: {
        LT: 'h:mm A',
        LTS: 'h:mm:ss A',
        L: 'DD/MM/YYYY',
        LL: 'MMMM Do YYYY',
        LLL: 'MMMM Do YYYY LT',
        LLLL: 'dddd, MMMM Do YYYY LT',
      },
      week: { dow: 1 },
    });

    // We want same setting for dutch locale
    // Default is: https://github.com/moment/moment/blob/develop/src/locale/nl.js
    moment.updateLocale('nl', {
      longDateFormat: {
        LT: 'h:mm A',
        LTS: 'h:mm:ss A',
        L: 'DD/MM/YYYY',
        LL: 'MMMM Do YYYY',
        LLL: 'MMMM Do YYYY LT',
        LLLL: 'dddd, MMMM Do YYYY LT',
      },
    });
  }

  private loadSvgIcons() {
    const icons = {
      'heart-outline': 'assets/images/svg/heart-outline.svg',
      'flag-se': 'assets/images/svg/flag-se.svg',
    };
    this.iconRegistry.addSvgIconSet(this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/sprite.svg'));
    Object.entries(icons).forEach(([name, path]) =>
      this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(path)),
    );
  }

  private loadThemeAndInfo() {
    this.appModeService.init();
    this.title.setTitle(this.translateService.translate('app.title'));
    this.meta.updateTag({ name: 'description', content: this.translateService.translate('app.description') });
  }

  private async loadUserAndLanguage() {
    if (this.oktaService.hasValidAccessToken()) {
      await lastValueFrom(this.authService.getRemoteUser(false));

      if (this.appInfoService.appInfo.isFeatureOn('useSignalR')) {
        const hubConnection = this.webSocketEventService.openThreadHubSocket(
          this.appInfoService.appInfo,
          this.oktaService.getAccessToken(),
        );
        if (hubConnection.state === HubConnectionState.Connected) {
          this.webSocketEventService.listenToNotifications();
        } else {
          hubConnection.start().then(() => this.webSocketEventService.listenToNotifications());
        }
      }
    }
    const lang = this.localStorageService.lang || getBrowserLang();
    // this line to make sure to load translations on first load and avoid missing trads
    // https://github.com/jsverse/transloco/issues/811
    await lastValueFrom(this.translateService.setActiveLang(lang).load(lang));
  }

  private whenLanguageChange(lang: string) {
    moment.locale(lang);
    this.document.querySelector('html').setAttribute('lang', lang);
    this.dateAdapter.setLocale(lang);
  }

  async init() {
    this.setupMoment();
    this.loadSvgIcons();
    await this.loadEnvConfig();
    await this.loadUserAndLanguage();
    this.loadThemeAndInfo();
    // TODO: this temporary till the unregister is properly propagated to clients
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }

  constructor() {
    // when language change
    this.translateService.langChanges$
      .pipe(
        takeUntilDestroyed(),
        tap((lang) => this.whenLanguageChange(lang)),
      )
      .subscribe();
  }
}
